import React, { useMemo } from 'react';
import {
  Paper, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Box, Chip,
} from '@mui/material';

const MONTH_LABELS = ['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'];

const fmtAmt = (v) => {
  if (!v && v !== 0) return '-';
  const n = Number(v);
  if (n === 0) return '-';
  if (Math.abs(n) >= 100000000) return `${(n / 100000000).toFixed(1)}억`;
  if (Math.abs(n) >= 10000) return `${(n / 10000).toFixed(0)}만`;
  return n.toLocaleString();
};

// 전년 대비 증감률
const calcRate = (curr, prev) => {
  if (!prev || Number(prev) === 0) return null;
  const rate = ((Number(curr) - Number(prev)) / Number(prev)) * 100;
  return rate.toFixed(1);
};

const KpiReportTable = ({ targetTyp, years, reportData, viewMode }) => {
  const [currYear, prevYear] = years;

  // 월별 테이블 데이터
  const monthlyRows = useMemo(() => {
    return MONTH_LABELS.map((label, idx) => {
      const month = String(idx + 1);
      const currList = reportData[currYear] || [];
      const prevList = reportData[prevYear] || [];

      const curr = currList.reduce((s, r) => s + (r[month] ? Number(r[month]) : 0), 0);
      const prev = prevList.reduce((s, r) => s + (r[month] ? Number(r[month]) : 0), 0);
      const rate = calcRate(curr, prev);

      return { label, curr, prev, rate };
    });
  }, [reportData, currYear, prevYear]);

  // 연도별 테이블 데이터
  const yearlyRows = useMemo(() => {
    return years.map((year) => {
      const list = reportData[year] || [];
      const total = list.reduce((s, r) => s + (r['total'] ? Number(r['total']) : 0), 0);
      return { label: `${year}년`, year, total };
    });
  }, [reportData, years]);

  // 합계
  const currTotal = useMemo(() =>
    monthlyRows.reduce((s, r) => s + r.curr, 0), [monthlyRows]);
  const prevTotal = useMemo(() =>
    monthlyRows.reduce((s, r) => s + r.prev, 0), [monthlyRows]);
  const totalRate = calcRate(currTotal, prevTotal);

  const RateChip = ({ rate }) => {
    if (rate === null) return <span style={{ color: '#999' }}>-</span>;
    const r = Number(rate);
    return (
      <Chip
        label={`${r >= 0 ? '+' : ''}${rate}%`}
        size="small"
        color={r >= 0 ? 'success' : 'error'}
        variant="outlined"
        sx={{ fontSize: '0.7rem', height: 20 }}
      />
    );
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} mb={2}>
        {viewMode === 'monthly' ? '월별 상세' : '연도별 상세'}
      </Typography>

      {viewMode === 'monthly' ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell align="center" sx={{ fontWeight: 700 }}>월</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  {currYear}년
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#ef6c00' }}>
                  {prevYear}년
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>전년 대비</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthlyRows.map((row) => (
                <TableRow key={row.label} hover>
                  <TableCell align="center">{row.label}</TableCell>
                  <TableCell align="right">{fmtAmt(row.curr)}</TableCell>
                  <TableCell align="right">{fmtAmt(row.prev)}</TableCell>
                  <TableCell align="center"><RateChip rate={row.rate} /></TableCell>
                </TableRow>
              ))}
              {/* 합계 행 */}
              <TableRow sx={{ backgroundColor: '#e3f2fd', fontWeight: 700 }}>
                <TableCell align="center" sx={{ fontWeight: 700 }}>합계</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtAmt(currTotal)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtAmt(prevTotal)}</TableCell>
                <TableCell align="center"><RateChip rate={totalRate} /></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell align="center" sx={{ fontWeight: 700 }}>연도</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>연간 합계</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>전년 대비</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {yearlyRows.map((row, idx) => (
                <TableRow key={row.label} hover>
                  <TableCell align="center">{row.label}</TableCell>
                  <TableCell align="right">{fmtAmt(row.total)}</TableCell>
                  <TableCell align="center">
                    {idx === 0
                      ? <RateChip rate={calcRate(yearlyRows[0]?.total, yearlyRows[1]?.total)} />
                      : <span style={{ color: '#999' }}>기준</span>
                    }
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default KpiReportTable;
